import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiUsers,
  FiBriefcase,
  FiFileText,
  FiUserCheck,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiX,
  FiShield,
  FiUser,
  FiBookOpen,
  FiStar,
  FiSend,
  FiImage,
} from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { NotificationContext } from "../context/NotificationContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import {
  Button,
  Card,
  Tabs,
  Input,
  Textarea,
  Modal,
  EmptyState,
  Spinner,
  Badge,
} from "../components/ui";
import {
  getCommunities,
  createCommunity,
  updateCommunity,
  deleteCommunity,
} from "../api/communities";
import { getJobs, createJob, updateJob, deleteJob } from "../api/jobs";
import { getAllApplications } from "../api/applications";
import {
  getAllJoinRequests,
  approveJoinRequest,
  rejectJoinRequest,
} from "../api/joinRequests";
import { toggleAdminRole, createUser } from "../api/admin";
import { getUsers } from "../api/users";
import { applicationFileUrl } from "../utils/assetUrl";
import { formatSmartTimestamp } from "../utils/format";
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
} from "../api/courses";
import {
  shortlistApplicant,
  updateApplicationStatus,
} from "../api/applications";
import { sendApplicationFeedback } from "../api/directMessages";

const TABS = [
  {
    value: "overview",
    label: "Overview",
    icon: <FiShield className="h-4 w-4" />,
    notifKey: null,
  },
  {
    value: "communities",
    label: "Communities",
    icon: <FiUsers className="h-4 w-4" />,
    notifKey: "admin_communities",
  },
  {
    value: "jobs",
    label: "Jobs",
    icon: <FiBriefcase className="h-4 w-4" />,
    notifKey: "admin_jobs",
  },
  {
    value: "requests",
    label: "Join requests",
    icon: <FiUserCheck className="h-4 w-4" />,
    notifKey: "admin_requests",
  },
  {
    value: "members",
    label: "Members",
    icon: <FiUser className="h-4 w-4" />,
    notifKey: null,
  },
  {
    value: "courses",
    label: "Courses",
    icon: <FiBookOpen className="h-4 w-4" />,
    notifKey: null,
  },
];

export default function Admin() {
  const { isAdmin, user: currentUser } = useContext(AuthContext);
  const { categoryCounts, markCategoryRead } = useContext(NotificationContext);
  const { notify } = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");

  // Each tab shows its own unread count (job applications, join requests,
  // community events/projects) instead of only the cumulative badge on the
  // sidebar's single Admin icon — so it's clear where a notification
  // actually belongs before you even open it.
  const tabsWithCounts = useMemo(
    () =>
      TABS.map((t) => ({
        ...t,
        count: t.notifKey ? categoryCounts[t.notifKey] || undefined : undefined,
      })),
    [categoryCounts],
  );

  // Opening a tab clears just that tab's badge, not every admin
  // notification at once.
  useEffect(() => {
    const current = TABS.find((t) => t.value === tab);
    if (current?.notifKey) markCategoryRead(current.notifKey);
  }, [tab, markCategoryRead]);

  const [communities, setCommunities] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestsError, setRequestsError] = useState(false);
  const [users, setUsers] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleBusyId, setRoleBusyId] = useState(null);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    birthday: "",
    roles: "user",
  });

  const [invitingUser, setInvitingUser] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");

  const [communityModal, setCommunityModal] = useState(null);
  const [communityForm, setCommunityForm] = useState({
    name: "",
    description: "",
  });
  const [communityImage, setCommunityImage] = useState(null);
  const [savingCommunity, setSavingCommunity] = useState(false);

  const [jobModal, setJobModal] = useState(null);
  const [jobForm, setJobForm] = useState({
    title: "",
    company: "",
    location: "",
    description: "",
  });
  const [savingJob, setSavingJob] = useState(false);

  const [applicationsFor, setApplicationsFor] = useState(null);
  const [feedbackApp, setFeedbackApp] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [shortlistBusyId, setShortlistBusyId] = useState(null);

  const [courses, setCourses] = useState([]);
  const [courseModal, setCourseModal] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    duration: "",
    category: "",
    tutor: "",
    languages: "English",
  });
  const [courseImage, setCourseImage] = useState(null);
  const [savingCourse, setSavingCourse] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.allSettled([
      getCommunities(),
      getJobs(),
      getUsers(),
      getAllApplications(),
      getCourses(),
    ]).then(([c, j, u, a, co]) => {
      if (c.status === "fulfilled") setCommunities(c.value.data?.data || []);
      if (j.status === "fulfilled") setJobs(j.value.data?.data || []);
      if (u.status === "fulfilled") setUsers(u.value.data?.data || []);
      if (a.status === "fulfilled")
        setAllApplications(a.value.data?.jobApplications || []);
      if (co.status === "fulfilled") setCourses(co.value.data?.courses || []);
      setLoading(false);
    });
    getAllJoinRequests()
      .then((res) =>
        setRequests(
          (res.data?.joinRequests || []).filter((r) => r.status === "Pending"),
        ),
      )
      .catch(() => setRequestsError(true));
  };

  // ── Courses ──────────────────────────────────────────────
  const openCourseModal = (course) => {
    setCourseModal(course || "new");
    setCourseForm(
      course
        ? {
            title: course.title,
            description: course.description || "",
            duration: course.duration || "",
            category: course.category || "",
            tutor: course.tutor || "",
            languages: course.languages || "English",
          }
        : {
            title: "",
            description: "",
            duration: "",
            category: "",
            tutor: "",
            languages: "English",
          },
    );
    setCourseImage(null);
  };

  const saveCourse = async () => {
    if (!courseForm.title.trim()) return;
    setSavingCourse(true);
    try {
      const fd = new FormData();
      Object.entries(courseForm).forEach(([k, v]) => fd.append(k, v));
      if (courseImage) fd.append("image", courseImage);
      if (courseModal === "new") {
        await createCourse(fd);
        notify("Course created.", "success");
      } else {
        await updateCourse(courseModal.id, fd);
        notify("Course updated.", "success");
      }
      setCourseModal(null);
      loadAll();
    } catch {
      notify("Couldn't save course.", "error");
    } finally {
      setSavingCourse(false);
    }
  };

  const removeCourse = async (id) => {
    if (
      !(await confirm({
        title: "Delete this course?",
        description:
          "This will permanently delete the course along with every learner's enrollment and progress. This cannot be undone.",
      }))
    )
      return;
    try {
      await deleteCourse(id);
      notify("Course deleted.", "success");
      loadAll();
    } catch {
      notify("Couldn't delete course.", "error");
    }
  };

  // ── Applications: shortlist + feedback ──────────────────────
  const handleShortlist = async (appId) => {
    setShortlistBusyId(appId);
    try {
      await shortlistApplicant(appId);
      setAllApplications((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, status: "shortlisted" } : a)),
      );
      notify("Applicant notified they've been shortlisted.", "success");
    } catch {
      notify("Couldn't shortlist this applicant.", "error");
    } finally {
      setShortlistBusyId(null);
    }
  };

  const [statusBusyId, setStatusBusyId] = useState(null);
  const handleSetStatus = async (appId, status) => {
    setStatusBusyId(appId);
    try {
      await updateApplicationStatus(appId, status);
      setAllApplications((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, status } : a)),
      );
      notify(`Applicant notified: ${status}.`, "success");
    } catch {
      notify("Couldn't update this application.", "error");
    } finally {
      setStatusBusyId(null);
    }
  };

  const openFeedback = (app) => {
    setFeedbackApp(app);
    setFeedbackText("");
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim() || !feedbackApp) return;
    setSendingFeedback(true);
    try {
      await sendApplicationFeedback(feedbackApp.id, feedbackText.trim());
      notify("Feedback sent to the applicant's inbox.", "success");
      setFeedbackApp(null);
    } catch (error) {
      notify(
        error.response?.data?.message ||
          "Couldn't send feedback — the applicant may not have an account.",
        "error",
      );
    } finally {
      setSendingFeedback(false);
    }
  };

  useEffect(loadAll, []);

  // Deep-linked from a notification like "New Job Application" — once jobs
  // have loaded, auto-open that job's applications modal.
  useEffect(() => {
    const jobId = searchParams.get("job");
    if (jobId && jobs.length > 0) {
      const job = jobs.find((j) => String(j.id) === jobId);
      if (job) setApplicationsFor(job);
    }
  }, [jobs, searchParams]);

  const openCommunityModal = (community) => {
    setCommunityModal(community || "new");
    setCommunityForm(
      community
        ? { name: community.name, description: community.description || "" }
        : { name: "", description: "" },
    );
    setCommunityImage(null);
  };

  const saveCommunity = async () => {
    if (!communityForm.name.trim()) return;
    setSavingCommunity(true);
    try {
      const formData = new FormData();
      formData.append("name", communityForm.name.trim());
      formData.append("description", communityForm.description.trim());
      if (communityImage) formData.append("image", communityImage);
      if (communityModal === "new") {
        await createCommunity(formData);
        notify("Community created.", "success");
      } else {
        await updateCommunity(communityModal.id, formData);
        notify("Community updated.", "success");
      }
      setCommunityModal(null);
      setCommunityForm({ name: "", description: "" });
      setCommunityImage(null);
      loadAll();
    } catch (error) {
      notify("Couldn't save the community.", "error");
    } finally {
      setSavingCommunity(false);
    }
  };

  const removeCommunity = async (id) => {
    if (
      !(await confirm({
        title: "Delete this community?",
        description:
          "This will permanently delete the community along with all its members, chat history, events, and projects. This cannot be undone.",
      }))
    )
      return;
    try {
      await deleteCommunity(id);
      notify("Community deleted.", "success");
      loadAll();
    } catch {
      notify("Couldn't delete this community.", "error");
    }
  };

  const openJobModal = (job) => {
    setJobModal(job || "new");
    setJobForm(
      job
        ? {
            title: job.title,
            company: job.company,
            location: job.location || "",
            description: job.description || "",
          }
        : { title: "", company: "", location: "", description: "" },
    );
  };

  const saveJob = async () => {
    if (!jobForm.title.trim() || !jobForm.company.trim()) return;
    setSavingJob(true);
    try {
      if (jobModal !== "new") {
        await updateJob(jobModal.id, jobForm);
        notify("Job updated.", "success");
      } else {
        await createJob(jobForm);
        notify("Job posted.", "success");
      }
      setJobModal(null);
      loadAll();
    } catch (error) {
      notify("Couldn't save this job.", "error");
    } finally {
      setSavingJob(false);
    }
  };

  const removeJob = async (id) => {
    if (
      !(await confirm({
        title: "Delete this job posting?",
        description:
          "This job and every application submitted to it will be permanently deleted. This cannot be undone.",
      }))
    )
      return;
    try {
      await deleteJob(id);
      notify("Job removed.", "success");
      loadAll();
    } catch {
      notify("Couldn't delete this job.", "error");
    }
  };

  const applicationsForJob = useMemo(() => {
    if (!applicationsFor) return [];
    return allApplications.filter((a) => a.jobId === applicationsFor.id);
  }, [allApplications, applicationsFor]);

  const [requestBusyId, setRequestBusyId] = useState(null);

  const handleApprove = async (request) => {
    setRequestBusyId(request.id);
    try {
      await approveJoinRequest(request.id);
      notify("Request approved — member added to the community.", "success");
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      loadAll();
    } catch (error) {
      notify(
        error.response?.data?.message || "Couldn't approve this request.",
        "error",
      );
    } finally {
      setRequestBusyId(null);
    }
  };

  const handleReject = async (id) => {
    setRequestBusyId(id);
    try {
      await rejectJoinRequest(id);
      notify("Request rejected.", "success");
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      notify(
        error.response?.data?.message || "Couldn't reject this request.",
        "error",
      );
    } finally {
      setRequestBusyId(null);
    }
  };

  const handleToggleRole = async (targetUser) => {
    setRoleBusyId(targetUser.id);
    try {
      await toggleAdminRole(targetUser.id);
      notify(
        `${targetUser.name} is ${targetUser.roles === "admin" ? "no longer an admin" : "now an admin"}.`,
        "success",
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === targetUser.id
            ? { ...u, roles: u.roles === "admin" ? "user" : "admin" }
            : u,
        ),
      );
    } catch {
      notify("Couldn't update this member's role.", "error");
    } finally {
      setRoleBusyId(null);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();

    if (
      !inviteForm.name ||
      !inviteForm.lastName ||
      !inviteForm.email ||
      !inviteForm.phone
    ) {
      notify("Please fill in all required fields.", "error");
      return;
    }

    try {
      setInvitingUser(true);

      const response = await createUser({
        name: inviteForm.name,
        lastName: inviteForm.lastName,
        email: inviteForm.email,
        phone: inviteForm.phone,
        birthday: inviteForm.birthday || null,
        roles: inviteForm.roles,
      });

      if (response.data.success) {
        notify("User account created successfully.", "success");
        setGeneratedPassword(response.data.temporaryPassword);

        setInviteForm({
          name: "",
          lastName: "",
          email: "",
          phone: "",
          birthday: "",
          roles: "user",
        });

        loadAll();
      }
    } catch (error) {
      notify(
        error.response?.data?.message || "Failed to create user account.",
        "error",
      );
    } finally {
      setInvitingUser(false);
    }
  };

  const stats = useMemo(
    () => [
      { label: "Communities", value: communities.length, icon: FiUsers },
      { label: "Open jobs", value: jobs.length, icon: FiBriefcase },
      { label: "Pending requests", value: requests.length, icon: FiUserCheck },
      { label: "Members", value: users.length, icon: FiUser },
    ],
    [communities, jobs, requests, users],
  );

  // What actually needs an admin's attention right now, derived from the
  // same per-tab notification counts that badge each tab — so "Overview"
  // is a real answer to "what's changed since I last looked", not just
  // four static totals.
  const attentionItems = useMemo(() => {
    const items = [];
    if (categoryCounts.admin_requests > 0) {
      items.push({
        tab: "requests",
        label: "New join requests",
        count: categoryCounts.admin_requests,
        icon: FiUserCheck,
      });
    }
    if (categoryCounts.admin_jobs > 0) {
      items.push({
        tab: "jobs",
        label: "New job applications",
        count: categoryCounts.admin_jobs,
        icon: FiBriefcase,
      });
    }
    if (categoryCounts.admin_communities > 0) {
      items.push({
        tab: "communities",
        label: "Community activity (events/projects)",
        count: categoryCounts.admin_communities,
        icon: FiUsers,
      });
    }
    return items;
  }, [categoryCounts]);

  if (!isAdmin) return null;

  return (
    <AppShell
      title="Admin portal"
      subtitle="Manage communities, jobs and membership requests"
      wide
    >
      <Tabs
        tabs={tabsWithCounts}
        active={tab}
        onChange={setTab}
        className="mb-6"
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  Needs your attention
                </h3>
                {attentionItems.length === 0 ? (
                  <Card className="flex items-center gap-3 p-5 text-sm text-slate-500">
                    <FiCheck className="h-4 w-4 text-emerald-500" /> You're all
                    caught up — nothing pending right now.
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {attentionItems.map(
                      ({ tab: targetTab, label, count, icon: Icon }) => (
                        <button
                          key={targetTab}
                          onClick={() => setTab(targetTab)}
                          className="flex items-center gap-3 rounded-lg border border-amber-100 bg-amber-50 p-4 text-left transition-colors hover:bg-amber-100"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-amber-600">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {label}
                            </p>
                            <p className="text-xs text-amber-700">
                              {count} unread
                            </p>
                          </div>
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  Platform totals
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {stats.map(({ label, value, icon: Icon }) => (
                    <Card key={label} className="flex items-center gap-4 p-5">
                      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">
                          {value}
                        </p>
                        <p className="text-sm text-slate-500">{label}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "communities" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button onClick={() => openCommunityModal(null)}>
                  <FiPlus className="h-4 w-4" /> New community
                </Button>
              </div>
              <Card className="divide-y divide-slate-100">
                {communities.length === 0 ? (
                  <EmptyState
                    className="border-none"
                    icon={<FiUsers className="h-6 w-6" />}
                    title="No communities yet"
                  />
                ) : (
                  communities.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 p-4"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {c.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {c.memberCount ?? c.members ?? 0} members
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="slate">{c.status || "active"}</Badge>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openCommunityModal(c)}
                        >
                          <FiEdit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outlineDanger"
                          onClick={() => removeCommunity(c.id)}
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </>
          )}

          {tab === "jobs" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button onClick={() => openJobModal(null)}>
                  <FiPlus className="h-4 w-4" /> Post a job
                </Button>
              </div>

              {jobs.length > 0 && (
                <Card className="mb-4 p-5">
                  <p className="mb-3 text-sm font-semibold text-slate-900">
                    Applicant interest by job
                  </p>
                  <div className="space-y-2.5">
                    {[...jobs]
                      .map((j) => ({
                        ...j,
                        count: allApplications.filter((a) => a.jobId === j.id)
                          .length,
                      }))
                      .sort((a, b) => b.count - a.count)
                      .map((j) => {
                        const max = Math.max(
                          1,
                          ...jobs.map(
                            (x) =>
                              allApplications.filter((a) => a.jobId === x.id)
                                .length,
                          ),
                        );
                        return (
                          <div key={j.id} className="flex items-center gap-3">
                            <span
                              className="w-32 shrink-0 truncate text-xs text-slate-600"
                              title={j.title}
                            >
                              {j.title}
                            </span>
                            <div className="h-2 flex-1 rounded-full bg-slate-100">
                              <div
                                className="h-2 rounded-full bg-primary-500 transition-all"
                                style={{ width: `${(j.count / max) * 100}%` }}
                              />
                            </div>
                            <span className="w-6 shrink-0 text-right text-xs font-semibold text-slate-700">
                              {j.count}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </Card>
              )}

              <Card className="divide-y divide-slate-100">
                {jobs.length === 0 ? (
                  <EmptyState
                    className="border-none"
                    icon={<FiBriefcase className="h-6 w-6" />}
                    title="No jobs posted yet"
                  />
                ) : (
                  jobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {job.title}
                        </p>
                        <p className="text-xs text-slate-400">
                          {job.company} &middot; {job.location || "Remote"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setApplicationsFor(job)}
                        >
                          <FiFileText className="h-3.5 w-3.5" /> Applications
                          {allApplications.filter((a) => a.jobId === job.id)
                            .length > 0 && (
                            <Badge variant="primary" className="ml-1">
                              {
                                allApplications.filter(
                                  (a) => a.jobId === job.id,
                                ).length
                              }
                            </Badge>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openJobModal(job)}
                        >
                          <FiEdit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outlineDanger"
                          onClick={() => removeJob(job.id)}
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </>
          )}

          {tab === "requests" && (
            <Card className="divide-y divide-slate-100">
              {requestsError ? (
                <EmptyState
                  className="border-none"
                  icon={<FiUserCheck className="h-6 w-6" />}
                  title="Join requests aren't available"
                  description="We couldn't reach the join-requests endpoint on the backend."
                />
              ) : requests.length === 0 ? (
                <EmptyState
                  className="border-none"
                  icon={<FiUserCheck className="h-6 w-6" />}
                  title="No pending requests"
                  description="You're all caught up."
                />
              ) : (
                requests.map((r) => {
                  const community = communities.find(
                    (c) => c.id === r.communityId,
                  );
                  return (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-3 p-4"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {r.joinRequestEmail || "A member"}
                        </p>
                        <p className="text-xs text-slate-400">
                          Wants to join{" "}
                          {community?.name || `community #${r.communityId}`}
                          {r.joinRequestMessage
                            ? ` — "${r.joinRequestMessage}"`
                            : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={requestBusyId === r.id}
                          disabled={requestBusyId === r.id}
                          onClick={() => handleApprove(r)}
                        >
                          <FiCheck className="h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outlineDanger"
                          loading={requestBusyId === r.id}
                          disabled={requestBusyId === r.id}
                          onClick={() => handleReject(r.id)}
                        >
                          <FiX className="h-3.5 w-3.5" /> Reject
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </Card>
          )}

          {tab === "members" && (
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    All registered users ({users.length})
                  </p>
                  <p className="text-xs text-slate-400">
                    Everyone with an account on the platform — not limited to
                    any one community.
                  </p>
                </div>

                <Button size="sm" onClick={() => setInviteModal(true)}>
                  <FiPlus className="h-4 w-4" />
                  Invite user
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-2.5">Name</th>
                      <th className="px-5 py-2.5">Contact</th>
                      <th className="px-5 py-2.5">Joined</th>
                      <th className="px-5 py-2.5">Role</th>
                      <th className="px-5 py-2.5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <EmptyState
                            className="border-none"
                            icon={<FiUser className="h-6 w-6" />}
                            title="No members yet"
                          />
                        </td>
                      </tr>
                    ) : (
                      users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-slate-800">
                              {u.name} {u.lastName}
                              {u.id === currentUser?.id && (
                                <span className="ml-2 text-xs font-normal text-slate-400">
                                  (you)
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="px-5 py-3 text-slate-500">
                            <p>{u.email}</p>
                            {u.phone && (
                              <p className="text-xs text-slate-400">
                                {u.phone}
                              </p>
                            )}
                          </td>
                          <td className="px-5 py-3 text-slate-500">
                            {u.createdAt
                              ? new Date(u.createdAt).toLocaleDateString(
                                  undefined,
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )
                              : "—"}
                          </td>
                          <td className="px-5 py-3">
                            <Badge
                              variant={
                                u.roles === "admin" ? "primary" : "slate"
                              }
                            >
                              {u.roles || "user"}
                            </Badge>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={roleBusyId === u.id}
                              disabled={u.id === currentUser?.id}
                              onClick={() => handleToggleRole(u)}
                            >
                              {u.roles === "admin"
                                ? "Revoke admin"
                                : "Make admin"}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {tab === "courses" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button onClick={() => openCourseModal(null)}>
                  <FiPlus className="h-4 w-4" /> Add course
                </Button>
              </div>
              {courses.length === 0 ? (
                <EmptyState
                  icon={<FiBookOpen className="h-6 w-6" />}
                  title="No courses yet"
                  description="Add the first course for members to enroll in."
                  action={
                    <Button onClick={() => openCourseModal(null)}>
                      <FiPlus className="h-4 w-4" /> Add course
                    </Button>
                  }
                />
              ) : (
                <div className="space-y-4">
                  {courses.map((course) => (
                    <Card key={course.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {course.title}
                          </p>
                          <p className="mt-0.5 text-sm text-slate-500">
                            {course.category}{" "}
                            {course.duration ? `· ${course.duration}` : ""}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openCourseModal(course)}
                          >
                            <FiEdit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outlineDanger"
                            onClick={() => removeCourse(course.id)}
                          >
                            <FiTrash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Enrolled learners ({course.enrollments?.length || 0})
                        </p>
                        {(course.enrollments?.length || 0) === 0 ? (
                          <p className="text-xs text-slate-400">
                            No one has enrolled yet.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {course.enrollments.map((e) => {
                              const learner = users.find(
                                (u) => u.id === e.userId,
                              );
                              return (
                                <div
                                  key={e.userId}
                                  className="flex items-center justify-between gap-3"
                                >
                                  <span className="text-sm text-slate-700">
                                    {learner
                                      ? `${learner.name} ${learner.lastName || ""}`
                                      : `User #${e.userId}`}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <div className="h-1.5 w-24 rounded-full bg-slate-100">
                                      <div
                                        className={`h-1.5 rounded-full ${e.completed ? "bg-emerald-500" : "bg-primary-400"}`}
                                        style={{ width: `${e.progress}%` }}
                                      />
                                    </div>
                                    <span className="w-16 text-right text-xs text-slate-400">
                                      {e.completed
                                        ? "Completed"
                                        : `${e.progress}%`}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Create/edit community modal */}
      <Modal
        open={Boolean(communityModal)}
        onClose={() => setCommunityModal(null)}
        title={
          communityModal === "new" ? "Create a community" : "Edit community"
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCommunityModal(null)}
              disabled={savingCommunity}
            >
              Cancel
            </Button>
            <Button onClick={saveCommunity} loading={savingCommunity}>
              {communityModal === "new" ? "Create" : "Save changes"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={communityForm.name}
            onChange={(e) =>
              setCommunityForm({ ...communityForm, name: e.target.value })
            }
          />
          <Textarea
            label="Description"
            value={communityForm.description}
            onChange={(e) =>
              setCommunityForm({
                ...communityForm,
                description: e.target.value,
              })
            }
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Cover image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCommunityImage(e.target.files?.[0] || null)}
            />
          </div>
        </div>
      </Modal>

      {/* Job modal */}
      <Modal
        open={Boolean(jobModal)}
        onClose={() => setJobModal(null)}
        title={jobModal === "new" ? "Post a new job" : "Edit job"}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setJobModal(null)}
              disabled={savingJob}
            >
              Cancel
            </Button>
            <Button onClick={saveJob} loading={savingJob}>
              Save job
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Job title"
            value={jobForm.title}
            onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
          />
          <Input
            label="Company"
            value={jobForm.company}
            onChange={(e) =>
              setJobForm({ ...jobForm, company: e.target.value })
            }
          />
          <Input
            label="Location"
            value={jobForm.location}
            onChange={(e) =>
              setJobForm({ ...jobForm, location: e.target.value })
            }
          />
          <Textarea
            label="Description"
            rows={4}
            value={jobForm.description}
            onChange={(e) =>
              setJobForm({ ...jobForm, description: e.target.value })
            }
          />
        </div>
      </Modal>

      {/* Applications modal */}
      <Modal
        open={Boolean(applicationsFor)}
        onClose={() => setApplicationsFor(null)}
        title={`Applications for ${applicationsFor?.title || ""}`}
        description={
          applicationsFor
            ? `${applicationsFor.company} · ${applicationsFor.location || "Remote"} · ${applicationsForJob.length} applicant${applicationsForJob.length === 1 ? "" : "s"}`
            : ""
        }
        size="lg"
      >
        {applicationsForJob.length === 0 ? (
          <EmptyState
            icon={<FiFileText className="h-6 w-6" />}
            title="No applications yet"
            description="Applicants will appear here as soon as they apply."
          />
        ) : (
          <div className="space-y-3">
            {applicationsForJob.map((app, i) => {
              const statusBadge = {
                pending: { variant: "slate", label: "Pending" },
                shortlisted: { variant: "primary", label: "Shortlisted" },
                accepted: { variant: "emerald", label: "Accepted" },
                rejected: { variant: "red", label: "Not Selected" },
              }[app.status || "pending"];
              return (
                <div
                  key={app.id || i}
                  className="rounded-lg border border-slate-100 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-800">
                          {app.name}
                        </p>
                        <Badge variant={statusBadge.variant}>
                          {statusBadge.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400">
                        {app.email} &middot; {app.phone}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Applied {formatSmartTimestamp(app.createdAt)}
                      </p>
                    </div>
                    <a
                      href={`/jobs?highlight=${app.jobId}`}
                      className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700"
                    >
                      View job posting
                    </a>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    {app.resume && (
                      <a
                        className="font-medium text-primary-600 hover:underline"
                        href={applicationFileUrl(app.resume)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        📄 Resume
                      </a>
                    )}
                    {app.coverLetter && (
                      <a
                        className="font-medium text-primary-600 hover:underline"
                        href={applicationFileUrl(app.coverLetter)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        📄 Cover letter
                      </a>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={shortlistBusyId === app.id}
                      disabled={app.status === "shortlisted"}
                      onClick={() => handleShortlist(app.id)}
                    >
                      <FiStar className="h-3.5 w-3.5" /> Shortlist
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={statusBusyId === app.id}
                      disabled={app.status === "accepted"}
                      onClick={() => handleSetStatus(app.id, "accepted")}
                    >
                      <FiCheck className="h-3.5 w-3.5" /> Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outlineDanger"
                      loading={statusBusyId === app.id}
                      disabled={app.status === "rejected"}
                      onClick={() => handleSetStatus(app.id, "rejected")}
                    >
                      <FiX className="h-3.5 w-3.5" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openFeedback(app)}
                    >
                      <FiSend className="h-3.5 w-3.5" /> Send feedback
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Feedback modal */}
      <Modal
        open={Boolean(feedbackApp)}
        onClose={() => setFeedbackApp(null)}
        title={`Message ${feedbackApp?.name || "applicant"}`}
        description="This will be sent to their inbox as a direct message and a notification."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setFeedbackApp(null)}
              disabled={sendingFeedback}
            >
              Cancel
            </Button>
            <Button
              onClick={submitFeedback}
              loading={sendingFeedback}
              disabled={!feedbackText.trim()}
            >
              Send
            </Button>
          </>
        }
      >
        <Textarea
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={4}
          placeholder="e.g. Thanks for applying — we'd like to schedule an interview…"
        />
      </Modal>

      {/* Course modal */}
      <Modal
        open={Boolean(courseModal)}
        onClose={() => setCourseModal(null)}
        title={courseModal === "new" ? "Add a course" : "Edit course"}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCourseModal(null)}
              disabled={savingCourse}
            >
              Cancel
            </Button>
            <Button onClick={saveCourse} loading={savingCourse}>
              Save course
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={courseForm.title}
            onChange={(e) =>
              setCourseForm({ ...courseForm, title: e.target.value })
            }
          />
          <Textarea
            label="Description"
            rows={3}
            value={courseForm.description}
            onChange={(e) =>
              setCourseForm({ ...courseForm, description: e.target.value })
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Duration"
              placeholder="e.g. 4 hours"
              value={courseForm.duration}
              onChange={(e) =>
                setCourseForm({ ...courseForm, duration: e.target.value })
              }
            />
            <Input
              label="Category"
              placeholder="e.g. Design"
              value={courseForm.category}
              onChange={(e) =>
                setCourseForm({ ...courseForm, category: e.target.value })
              }
            />
            <Input
              label="Tutor / Instructor"
              value={courseForm.tutor}
              onChange={(e) =>
                setCourseForm({ ...courseForm, tutor: e.target.value })
              }
            />
            <Input
              label="Language(s)"
              value={courseForm.languages}
              onChange={(e) =>
                setCourseForm({ ...courseForm, languages: e.target.value })
              }
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Thumbnail image
            </label>
            <label className="flex h-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400 hover:border-primary-300 hover:text-primary-500">
              <FiImage className="h-5 w-5" />
              <span className="text-xs">
                {courseImage ? courseImage.name : "Click to upload"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setCourseImage(e.target.files?.[0] || null)}
              />
            </label>
          </div>
        </div>
      </Modal>
      {/* invite user modal */}
      <Modal
        open={inviteModal}
        onClose={() => setInviteModal(false)}
        title="Invite user"
      >
        <form onSubmit={handleInviteUser} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={inviteForm.name}
              onChange={(e) =>
                setInviteForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />

            <Input
              label="Last name"
              value={inviteForm.lastName}
              onChange={(e) =>
                setInviteForm((prev) => ({ ...prev, lastName: e.target.value }))
              }
              required
            />
          </div>

          <Input
            label="Email"
            type="email"
            value={inviteForm.email}
            onChange={(e) =>
              setInviteForm((prev) => ({ ...prev, email: e.target.value }))
            }
            required
          />

          <Input
            label="Phone"
            value={inviteForm.phone}
            onChange={(e) =>
              setInviteForm((prev) => ({ ...prev, phone: e.target.value }))
            }
            required
          />

          <Input
            label="Birthday"
            type="date"
            value={inviteForm.birthday}
            onChange={(e) =>
              setInviteForm((prev) => ({ ...prev, birthday: e.target.value }))
            }
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Role
            </label>

            <select
              value={inviteForm.roles}
              onChange={(e) =>
                setInviteForm((prev) => ({ ...prev, roles: e.target.value }))
              }
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {generatedPassword && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-slate-900">
                Temporary password
              </p>

              <p className="mt-1 text-xs text-slate-500">
                Copy this password and share it securely with the user. They
                will be required to change it when they first log in.
              </p>

              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-900">
                  {generatedPassword}
                </code>

                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPassword);
                    notify("Temporary password copied.", "success");
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setInviteModal(false)}
            >
              Cancel
            </Button>

            <Button type="submit" disabled={invitingUser}>
              {invitingUser ? "Creating..." : "Create account"}
            </Button>
          </div>
        </form>
      </Modal>
    </AppShell>
  );
}
